import { useCallback, useRef, useState } from "react";

import { BlsMessage, BlsSettings, BlsState, mayControlBls } from "../domain/blsChannel";
import { clampCyclesPerMinute, cyclesAt } from "../domain/blsMotion";

export type BlsChannel = {
    /** What is up, on both screens. Null when nothing is. */
    state: BlsState | null;
    localIdentity: string;
    /** Whether this participant drives it — true for the therapist. */
    canControl: boolean;
    /** True while packets are failing to reach the other side. */
    transportFailed: boolean;
    /** The therapist's side: the patient pressed stop since the last start or resume. */
    haltedByPatient: boolean;
    /** The therapist's side: controllers the patient has ready, or null before they say. */
    patientControllers: number | null;
    /** The patient's side: tell the therapist how many controllers are ready. */
    reportControllers: (controllers: number) => void;
    /** How many cycles the set has completed at `nowEpochMs` on this machine's clock. */
    cyclesNow: (nowEpochMs?: number) => number;
    /** Begin a new set at the left edge. */
    start: (settings: BlsSettings) => void;
    pause: () => void;
    resume: () => void;
    /** Change speed, path, mode or look without moving the dot. */
    updateSettings: (settings: BlsSettings) => void;
    /** Take it down for everyone. */
    stop: () => void;
    /** Stop the dot. Anyone may — it is the patient's way out. */
    halt: () => void;
};

/** The state, and when it landed on this machine — the clock `cyclesAt` measures from. */
type Held = { state: BlsState; receivedAt: number } | null;

/** Sends one message to the other side; a rejected promise means it did not get there. */
export type BlsSend = (message: BlsMessage) => Promise<void> | void;

/** What a transport tells the session, on top of the channel itself. */
export type BlsSession = BlsChannel & {
    /** A message arrived from `sender`, already validated. */
    receive: (message: BlsMessage, sender: string) => void;
    /** The transport is (again) able to carry messages: put both sides back in step. */
    announce: () => void;
    /** Somebody left; if they drove the set, it is over. */
    peerLeft: (identity: string) => void;
};

/**
 * Keeping both screens on the same set, whatever carries the messages.
 *
 * The therapist's state is authoritative, every message carries all of it so
 * a dropped packet costs one stale frame instead of a permanent disagreement,
 * and a late joiner asks for it when the transport is ready. The one
 * exception is `halt`, which anybody may send: a patient mid-set must be able
 * to stop it without asking.
 *
 * Transports are thin: the LiveKit data channel inside a call, a
 * BroadcastChannel between two windows of one browser on the standalone board,
 * and a polled mailbox on our server for a board shared by link.
 */
export function useBlsSession(send: BlsSend, readIdentity: () => string): BlsSession {
    const localIdentity = readIdentity();
    const [held, setHeld] = useState<Held>(null);
    const [transportFailed, setTransportFailed] = useState(false);
    const [haltedByPatient, setHaltedByPatient] = useState(false);
    const [patientControllers, setPatientControllers] = useState<number | null>(null);
    // The patient's own count, re-sent whenever the room reconnects.
    const ownControllersRef = useRef<number | null>(null);

    const canControl = mayControlBls(localIdentity);
    /*
     * Read at call time, not captured: the identity is empty until the room
     * has connected, and a handler built in that first render would go on
     * treating the therapist as a patient — ignoring the patient's stop.
     */
    const readIdentityRef = useRef(readIdentity);
    readIdentityRef.current = readIdentity;
    const isController = useCallback(() => mayControlBls(readIdentityRef.current()), []);
    const sendRef = useRef(send);
    sendRef.current = send;
    // Read from callbacks that must not be rebuilt on every state change.
    const heldRef = useRef<Held>(null);
    const hold = useCallback((next: Held) => {
        heldRef.current = next;
        setHeld(next);
    }, []);

    const failing = useRef(false);
    const markTransport = useCallback((failed: boolean) => {
        if (failing.current === failed) return;
        failing.current = failed;
        setTransportFailed(failed);
    }, []);

    const publish = useCallback(
        (message: BlsMessage) => {
            try {
                const result = sendRef.current(message);
                if (result && typeof result.then === "function") {
                    result.then(
                        () => markTransport(false),
                        () => markTransport(true)
                    );
                }
            } catch {
                markTransport(true);
            }
        },
        [markTransport]
    );

    const currentCycles = useCallback((now: number): number => {
        const current = heldRef.current;
        return current ? cyclesAt(current.state, current.receivedAt, now) : 0;
    }, []);

    /** Hold and announce a new therapist state, measured now. */
    const commit = useCallback(
        (settings: BlsSettings, running: boolean, cycles: number) => {
            const now = Date.now();
            const state: BlsState = {
                settings: { ...settings, cyclesPerMinute: clampCyclesPerMinute(settings.cyclesPerMinute) },
                running,
                cycles,
                atEpochMs: now,
            };
            hold({ state, receivedAt: now });
            publish({ kind: "state", state });
        },
        [hold, publish]
    );

    /** Freeze the dot where it is, on this screen only. */
    const freeze = useCallback((): BlsState | null => {
        const current = heldRef.current;
        if (!current) return null;
        const now = Date.now();
        const state: BlsState = { ...current.state, running: false, cycles: currentCycles(now), atEpochMs: now };
        hold({ state, receivedAt: now });
        return state;
    }, [currentCycles, hold]);

    const receive = useCallback(
        (message: BlsMessage, sender: string) => {

            switch (message.kind) {
                case "state":
                    if (mayControlBls(sender)) hold({ state: message.state, receivedAt: Date.now() });
                    break;
                case "stop":
                    if (mayControlBls(sender)) hold(null);
                    break;
                case "halt": {
                    // Only the therapist's browser acts on it, by pausing and
                    // announcing the paused state — so the therapist stays the
                    // one source of truth and every patient screen agrees.
                    if (!isController() || !heldRef.current?.state.running) return;
                    const paused = freeze();
                    if (paused) publish({ kind: "state", state: paused });
                    setHaltedByPatient(true);
                    break;
                }
                case "tactile-status":
                    if (isController() && !mayControlBls(sender)) setPatientControllers(message.controllers);
                    break;
                case "sync-request":
                    if (!isController()) return;
                    if (heldRef.current) {
                        const { settings, running } = heldRef.current.state;
                        commit(settings, running, currentCycles(Date.now()));
                    } else {
                        publish({ kind: "stop" });
                    }
                    break;
            }
        },
        [isController, commit, currentCycles, freeze, hold, publish]
    );

    const announce = useCallback(() => {
        if (!isController()) {
            publish({ kind: "sync-request" });
            const controllers = ownControllersRef.current;
            if (controllers !== null) publish({ kind: "tactile-status", controllers });
            return;
        }
        // After a reload the therapist has nothing up, and a patient still
        // showing the last set would sit in front of a dot nobody controls.
        const current = heldRef.current;
        if (current) {
            commit(current.state.settings, current.state.running, currentCycles(Date.now()));
        } else {
            publish({ kind: "stop" });
        }
    }, [isController, commit, currentCycles, publish]);

    /** A set outlives nobody who drives it. */
    const peerLeft = useCallback(
        (identity: string) => {
            if (!isController() && mayControlBls(identity)) hold(null);
        },
        [isController, hold]
    );

    const start = useCallback(
        (settings: BlsSettings) => {
            if (!isController()) return;
            setHaltedByPatient(false);
            commit(settings, true, 0);
        },
        [isController, commit]
    );

    const pause = useCallback(() => {
        if (!isController()) return;
        const paused = freeze();
        if (paused) publish({ kind: "state", state: paused });
    }, [isController, freeze, publish]);

    const resume = useCallback(() => {
        const current = heldRef.current;
        if (!isController() || !current) return;
        setHaltedByPatient(false);
        commit(current.state.settings, true, current.state.cycles);
    }, [isController, commit]);

    const updateSettings = useCallback(
        (settings: BlsSettings) => {
            const current = heldRef.current;
            if (!isController() || !current) return;
            commit(settings, current.state.running, currentCycles(Date.now()));
        },
        [isController, commit, currentCycles]
    );

    const stop = useCallback(() => {
        if (!isController()) return;
        hold(null);
        setHaltedByPatient(false);
        publish({ kind: "stop" });
    }, [isController, hold, publish]);

    const halt = useCallback(() => {
        if (!heldRef.current?.state.running) return;
        if (isController()) {
            pause();
            return;
        }
        freeze();
        publish({ kind: "halt" });
    }, [isController, freeze, pause, publish]);

    const reportControllers = useCallback(
        (controllers: number) => {
            // Kept even before we know who we are, so the announcement made
            // once the room is connected carries it.
            ownControllersRef.current = controllers;
            if (isController() || !readIdentityRef.current()) return;
            publish({ kind: "tactile-status", controllers });
        },
        [isController, publish]
    );

    const cyclesNow = useCallback(
        (nowEpochMs: number = Date.now()) => currentCycles(nowEpochMs),
        [currentCycles]
    );

    return {
        state: held?.state ?? null,
        localIdentity,
        canControl,
        transportFailed,
        haltedByPatient,
        patientControllers,
        reportControllers,
        cyclesNow,
        receive,
        announce,
        peerLeft,
        start,
        pause,
        resume,
        updateSettings,
        stop,
        halt,
    };
}
